import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import {
  EventBySlugQuery,
  GetAlbumDetailsQuery,
  GetHeroImageQuery,
} from "@src/@types/api.d";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import defaultImg from "../../../../public/images/bw-flower.jpeg";

interface IWelcome {
  slugData?: EventBySlugQuery | null;
  heroImageData?: GetHeroImageQuery | null;
  albumDetailsData?: GetAlbumDetailsQuery | null;
}

const Welcome: React.FC<IWelcome> = ({
  slugData,
  heroImageData,
  albumDetailsData,
}) => {
  const welcomeMsg = slugData?.eventBySlug?.welcome_msg;
  const eventName = slugData?.eventBySlug?.name;

  // Pick first two valid album images
  const validAlbumImages = React.useMemo(() => {
    return (albumDetailsData?.getMedia || [])
      .filter(
        (m) =>
          m &&
          typeof m?.type === "string" &&
          m.type.toLowerCase().includes("image") &&
          m?.meta?.s3_url?.startsWith("http")
      )
      .slice(0, 2);
  }, [albumDetailsData]);

  const leftImage =
    validAlbumImages[0]?.meta?.s3_url ||
    // heroImageData?.getHeroImage ||
    // slugData?.eventBySlug?.hero_image_thumbnail ||
    null;

  const rightImage =
    validAlbumImages[1]?.meta?.s3_url ||
    // slugData?.eventBySlug?.pass_image ||
    null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section className="min-h-[calc(100vh-96px)] md:min-h-[calc(100vh-112px)] bg-transparent flex flex-col items-center justify-center px-4 md:px-6 pb-8 md:pb-10">
      <motion.div
        className="w-full max-w-2xl flex flex-col items-center gap-8 md:gap-10 mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Couple images */}
        <motion.div
          className="w-full flex items-center justify-center gap-0 md:gap-8 lg:gap-16"
          variants={itemVariants}
        >
          {/* Left image */}
          <div className="relative w-[62vw] max-w-[260px] md:w-[38vw] md:max-w-[220px] lg:max-w-[300px]">
            <div
              className="relative w-full overflow-hidden rounded-[40%_60%_60%_40%/40%_40%_60%_60%] shadow-xl"
              style={{ paddingBottom: "140%" }}
            >
              <Image
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(400, 560)
                )}`}
                src={leftImage || defaultImg}
                alt="Couple photo 1"
                layout="fill"
                objectFit="cover"
                objectPosition="center top"
                onError={(event) => {
                  const target = event.currentTarget as HTMLImageElement;
                  if (!target.src.includes("bw-flower.jpeg")) {
                    target.src = defaultImg.src;
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/20 to-transparent" />
            </div>
          </div>

          {/* Centre divider */}
          <div className="hidden md:flex flex-col items-center gap-2 shrink-0">
            <div className="w-px h-20 lg:h-32 bg-[#0B1F3A]/30" />
            <div className="w-2 h-2 rounded-full bg-[#0B1F3A]/50" />
            <div className="w-px h-20 lg:h-32 bg-[#0B1F3A]/30" />
          </div>

          {/* Right image */}
          <div className="hidden md:block relative md:w-[38vw] md:max-w-[220px] lg:max-w-[300px]">
            <div
              className="relative w-full overflow-hidden rounded-[60%_40%_40%_60%/40%_40%_60%_60%] shadow-xl"
              style={{ paddingBottom: "140%" }}
            >
              <Image
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(400, 560)
                )}`}
                src={rightImage || defaultImg}
                alt="Couple photo 2"
                layout="fill"
                objectFit="cover"
                objectPosition="center top"
                onError={(event) => {
                  const target = event.currentTarget as HTMLImageElement;
                  if (!target.src.includes("bw-flower.jpeg")) {
                    target.src = defaultImg.src;
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/20 to-transparent" />
            </div>
          </div>
        </motion.div>

        {/* Event name */}
        {/* {eventName && (
          <motion.h1
            className="text-[#b18958] text-2xl md:text-3xl font-semibold text-center tracking-wide"
            variants={itemVariants}
          >
            {eventName}
          </motion.h1>
        )} */}

        {/* Divider */}
        {/* <motion.div
          className="flex items-center gap-3 w-full max-w-xs"
          variants={itemVariants}
        >
          <div className="flex-1 h-px bg-[#0B1F3A]/30" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#0B1F3A]/50" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#0B1F3A]/80" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#0B1F3A]/50" />
          <div className="flex-1 h-px bg-[#0B1F3A]/30" />
        </motion.div> */}

        {/* Welcome message */}
        {welcomeMsg && (
          <motion.p
            className="text-[#b18958] text-base md:text-lg text-center leading-relaxed max-w-prose italic bg-white px-5 py-4 md:px-6 md:py-5 rounded-md border border-[#123B66]/20 shadow-sm"
            variants={itemVariants}
          >
            {welcomeMsg}
          </motion.p>
        )}
      </motion.div>
    </section>
  );
};

export default Welcome;
